import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  CreditCard, 
  TrendingUp, 
  DollarSign, 
  PiggyBank, 
  Wallet, 
  CheckCircle, 
  Star,
  ArrowUpDown,
  Lock,
  Globe,
  Zap,
  Bot,
  LineChart,
  Shield,
  ExternalLink,
  ArrowRight,
  Percent,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface ServiceTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
}

export function InfraBankHub() {
  const [selectedService, setSelectedService] = useState<string>('premium');

  const accountTiers: ServiceTier[] = [
    {
      name: 'Basic Account',
      price: 'Free',
      description: 'Essential banking for crypto enthusiasts',
      features: [
        'Multi-currency wallet (Fiat & Crypto)',
        'Basic debit card',
        'Up to $10K monthly transactions',
        'Standard crypto trading',
        '2% cashback on purchases',
        'Mobile & web access',
        'Email support'
      ]
    },
    {
      name: 'Premium Account',
      price: '$49',
      description: 'Advanced features for active traders',
      features: [
        'Everything in Basic',
        'Premium metal card',
        'Up to $100K monthly transactions',
        'Advanced trading tools & analytics',
        '5% cashback + rewards',
        'Access to investment bots',
        'Personal loans up to $50K',
        'Priority support 24/7',
        'No foreign exchange fees'
      ],
      highlighted: true
    },
    {
      name: 'Elite Account',
      price: '$199',
      description: 'Exclusive banking for high-net-worth individuals',
      features: [
        'Everything in Premium',
        'Black titanium card',
        'Unlimited transactions',
        'Private investment bots (custom strategies)',
        '10% cashback + exclusive perks',
        'Personal loans up to $500K',
        'Dedicated relationship manager',
        'Concierge services',
        'Early access to new products',
        'Private investment opportunities'
      ]
    }
  ];

  const services = [
    {
      icon: CreditCard,
      title: 'Premium Cards',
      description: 'Physical and virtual cards with up to 10% cashback on crypto and fiat purchases.',
      color: 'cyan',
      badge: 'Popular'
    },
    {
      icon: Bot,
      title: 'Investment Bots',
      description: 'AI-powered private investment bots with custom strategies tailored to your portfolio.',
      color: 'blue',
      badge: 'New'
    },
    {
      icon: TrendingUp,
      title: 'Smart Investments',
      description: 'Diversified portfolios with crypto, stocks, bonds, and alternative assets.',
      color: 'cyan',
      badge: null
    },
    {
      icon: DollarSign,
      title: 'Flexible Loans',
      description: 'Crypto-backed and traditional loans with competitive rates from 3.5% APR.',
      color: 'blue',
      badge: null
    },
    {
      icon: PiggyBank,
      title: 'High-Yield Savings',
      description: 'Earn up to 8% APY on crypto deposits and 4% on fiat savings accounts.',
      color: 'cyan',
      badge: 'Featured'
    },
    {
      icon: LineChart,
      title: 'Trading Platform',
      description: 'Professional trading tools with advanced charts, margin trading, and real-time data.',
      color: 'blue',
      badge: null
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Bank-Grade Security',
      description: 'Multi-layer security with insurance up to $500K per account'
    },
    {
      icon: Globe,
      title: 'Global Access',
      description: 'Available in 100+ countries with multi-currency support'
    },
    {
      icon: Zap,
      title: 'Instant Transfers',
      description: 'Lightning-fast fiat to crypto conversions in seconds'
    },
    {
      icon: Percent,
      title: 'Low Fees',
      description: 'Competitive rates with no hidden charges or monthly minimums'
    }
  ];

  const investmentBots = [
    {
      name: 'Conservative Bot',
      strategy: 'Low-risk, steady returns',
      expectedReturn: '8-12% APY',
      riskLevel: 'Low'
    },
    {
      name: 'Balanced Bot',
      strategy: 'Moderate risk, balanced portfolio',
      expectedReturn: '15-25% APY',
      riskLevel: 'Medium'
    },
    {
      name: 'Aggressive Bot',
      strategy: 'High-risk, maximum returns',
      expectedReturn: '30-50% APY',
      riskLevel: 'High'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 via-blue-700 to-indigo-900">
          <motion.div
            className="absolute inset-0 bg-gradient-to-tr from-cyan-500/30 via-transparent to-blue-500/30"
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Animated particles */}
          <div className="absolute inset-0">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white/20 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 px-8 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mb-6">
              <Building2 className="w-5 h-5 text-cyan-200" />
              <span className="text-sm font-semibold text-white">INFRA TECHNOLOGY Group</span>
            </div>

            <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-white via-cyan-100 to-blue-100 bg-clip-text text-transparent">
              INFRABANK
            </h1>

            <p className="text-2xl text-white/90 mb-6 max-w-3xl mx-auto">
              Next-Generation Fiat & Crypto Banking
            </p>

            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              The complete banking solution merging traditional finance with cryptocurrency. 
              Investments, cards, loans, and exclusive private investment bots.
            </p>

            <div className="flex gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-cyan-700 hover:bg-cyan-50"
              >
                <Wallet className="mr-2 h-5 w-5" />
                Explore Services
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                disabled
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Open Account (Coming Soon)
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Partnership Badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-cyan-200 dark:border-cyan-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-3">
              <Star className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              <p className="text-lg font-semibold text-cyan-900 dark:text-cyan-100">
                Trusted Partner of Nardium - Powered by INFRA TECHNOLOGY
              </p>
              <Star className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Key Benefits */}
      <div>
        <h2 className="text-3xl font-bold mb-6 text-center">Why Choose INFRABANK</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-cyan-200 dark:border-cyan-800 hover:shadow-xl hover:shadow-cyan-500/20 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-cyan-900 dark:text-cyan-100">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Account Tiers */}
      <div>
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-4">Choose Your Account</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            From basic banking to elite privileges. Find the perfect account for your financial goals.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {accountTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={tier.highlighted ? 'md:scale-105' : ''}
            >
              <Card 
                className={`h-full relative overflow-hidden cursor-pointer transition-all ${
                  tier.highlighted 
                    ? 'border-cyan-500 dark:border-cyan-400 shadow-2xl shadow-cyan-500/30' 
                    : 'border-cyan-200 dark:border-cyan-800 hover:border-cyan-400'
                }`}
                onClick={() => setSelectedService(tier.name)}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-1 rounded-bl-lg">
                    <span className="text-sm font-semibold">MOST POPULAR</span>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="text-2xl text-cyan-900 dark:text-cyan-100">
                    {tier.name}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {tier.description}
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-cyan-600 dark:text-cyan-400">
                      {tier.price}
                    </span>
                    {tier.price !== 'Free' && (
                      <span className="text-slate-600 dark:text-slate-400 ml-2">/month</span>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full ${
                      tier.highlighted 
                        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700' 
                        : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300'
                    }`}
                    size="lg"
                    disabled
                  >
                    Open Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Services Grid */}
      <div>
        <h2 className="text-3xl font-bold mb-6 text-center">Our Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-cyan-200 dark:border-cyan-800 hover:shadow-lg hover:shadow-cyan-500/10 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      {service.badge && (
                        <Badge className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300">
                          {service.badge}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-cyan-900 dark:text-cyan-100">
                      {service.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Investment Bots Section */}
      <Card className="border-cyan-200 dark:border-cyan-800">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl text-cyan-900 dark:text-cyan-100">
                Private Investment Bots
              </CardTitle>
              <CardDescription>
                AI-powered automated trading with custom strategies
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {investmentBots.map((bot, index) => (
              <motion.div
                key={bot.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-cyan-300 dark:border-cyan-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-cyan-900 dark:text-cyan-100">{bot.name}</h4>
                      <Badge 
                        variant="outline"
                        className={`
                          ${bot.riskLevel === 'Low' ? 'border-green-500 text-green-700 dark:text-green-400' : ''}
                          ${bot.riskLevel === 'Medium' ? 'border-yellow-500 text-yellow-700 dark:text-yellow-400' : ''}
                          ${bot.riskLevel === 'High' ? 'border-red-500 text-red-700 dark:text-red-400' : ''}
                        `}
                      >
                        {bot.riskLevel} Risk
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      {bot.strategy}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 dark:text-slate-400">Expected Return</span>
                      <span className="font-bold text-cyan-600 dark:text-cyan-400">{bot.expectedReturn}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 text-center">
            * Returns are estimates based on historical performance. Not guaranteed. Premium & Elite accounts only.
          </p>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-700 border-0">
        <CardContent className="p-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Building2 className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Transform Your Banking?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join the future of finance where traditional banking meets cryptocurrency innovation.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-cyan-700 hover:bg-cyan-50"
                disabled
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Open Account (Coming Soon)
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Users className="mr-2 h-5 w-5" />
                Contact Us
              </Button>
            </div>
          </motion.div>
        </CardContent>
      </Card>

      {/* Footer Info */}
      <Card className="border-cyan-200 dark:border-cyan-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-cyan-900 dark:text-cyan-100">INFRABANK</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Part of INFRA TECHNOLOGY Group</p>
              </div>
            </div>
            <Badge className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300">
              🏦 Platform Launch: Q2 2025
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
